/**
 * The trade application page, in Horizon's checkout furniture.
 *
 * <p>Anatomy and metrics are the approved mockup's
 * (`solar/docs/design/redesign-2026-10-01/trade.html`, with `css/tokens.css` and
 * `css/shared.css`): the two-column `.layout-checkout`, the numbered `.co-panel` cards with
 * their mono `.co-num`, the `.form-grid` / `.field` / `.span-2` form furniture, the
 * `.choice-tile` radios, the `label.check` declaration, the file input with its
 * `::file-selector-button`, and the `.totals` aside with its `.assure-mini` ticks and
 * `.aside-note` pointers.
 *
 * <p>Every class is renamed into this block's own `ae-apply__…` namespace rather than
 * borrowed from the mockup's generic names. The mockup is a standalone document whose
 * `.field` and `.btn` own the page; this stylesheet ships into a storefront that already
 * has platform blocks of its own in the same document, and a rule on a name as common as
 * `.field` would reach them. The one exception is the shared `.ae` scope, which is where
 * every colour comes from.
 *
 * <p>No colour is chosen here. Each one resolves through the `--ae-*` scope declared in
 * `TradeStyles.BASE_CSS`, which reads the tenant's design tokens first, so the Horizon dark
 * default and the Horizon Day palette both come out right without this file knowing which
 * is on. The literal values that remain are geometry: the mockup's 4px spacing ladder and
 * its two breakpoints (768px for the two-column form grid, 1024px for the aside).
 */
export const APPLY_CSS = `
.ae-apply{display:block;padding-block:32px}
.ae-apply__wrap{max-width:var(--ae-max);margin-inline:auto;padding-inline:var(--ae-gutter)}

/* ── The section head: eyebrow, heading, lede ─────────────────────────────── */
.ae-apply__head{margin-bottom:24px;max-width:52em}
.ae-apply__eyebrow{display:block;margin:0 0 8px;font-size:.75rem;font-weight:650;letter-spacing:.08em;text-transform:uppercase;line-height:1.3;color:var(--ae-accent-ink)}
.ae-apply__title{margin:0;font-size:clamp(1.625rem,1.1rem + 1.8vw,2.375rem);line-height:1.12;font-weight:680;letter-spacing:-.02em;color:var(--ae-ink)}
.ae-apply__lede{margin:8px 0 0;color:var(--ae-muted);max-width:44em}

/* ── The two columns: panels, then the aside ──────────────────────────────── */
.ae-apply__layout{display:grid;grid-template-columns:1fr;gap:24px;align-items:start}
@media(min-width:1024px){
  .ae-apply__layout{grid-template-columns:minmax(0,1fr) 384px;gap:48px}
  .ae-apply__aside{position:sticky;top:96px}
}
.ae-apply__form{display:block;min-width:0}

/* ── A numbered panel ─────────────────────────────────────────────────────── */
.ae-apply__panel{background:var(--ae-surface);border:1px solid var(--ae-border);border-radius:var(--ae-radius-lg);box-shadow:var(--ae-shadow-sm);padding:24px;min-width:0;margin:0}
.ae-apply__panel+.ae-apply__panel{margin-top:24px}
.ae-apply__panel>h3{display:flex;align-items:baseline;gap:12px;margin:0 0 16px;font-size:1.25rem;font-weight:650;letter-spacing:-.01em;color:var(--ae-ink)}
.ae-apply__num{font-family:var(--font-family-mono,ui-monospace,SFMono-Regular,Menlo,monospace);font-size:.8125rem;font-weight:600;letter-spacing:.08em;color:var(--ae-accent-ink);font-variant-numeric:tabular-nums}
.ae-apply__sub{margin:-4px 0 16px;color:var(--ae-muted);font-size:.875rem;max-width:44em}

/* ── Form furniture ───────────────────────────────────────────────────────── */
.ae-apply__grid{display:grid;grid-template-columns:1fr;gap:0 16px}
@media(min-width:768px){
  .ae-apply__grid{grid-template-columns:1fr 1fr}
  .ae-apply__grid>.ae-apply__span2{grid-column:1 / -1}
}
.ae-apply__field{display:grid;gap:6px;margin-bottom:16px;min-width:0}
.ae-apply__field>label{font-size:.875rem;font-weight:600;color:var(--ae-ink)}
.ae-apply__opt{color:var(--ae-muted);font-weight:400}
.ae-apply__hint{margin:0;font-size:.8125rem;color:var(--ae-muted)}
.ae-apply__field input:not([type="checkbox"]):not([type="radio"]),
.ae-apply__field select,
.ae-apply__field textarea{
  width:100%;min-height:44px;padding:10px 14px;font:inherit;font-size:16px;
  color:var(--ae-ink);background:var(--ae-surface);
  border:1px solid var(--ae-border-strong);border-radius:var(--ae-radius)}
.ae-apply__field textarea{min-height:96px;resize:vertical}
.ae-apply__field :where(input,select,textarea):focus-visible{outline:2px solid var(--ae-focus);outline-offset:0;border-color:var(--ae-focus)}
.ae-apply__field input[type="file"]{padding:8px 12px}
.ae-apply__field input[type="file"]::file-selector-button{
  font:inherit;font-size:.875rem;font-weight:600;color:var(--ae-ink);background:var(--ae-subtle);
  border:1px solid var(--ae-border-strong);border-radius:var(--ae-radius);
  padding:8px 16px;margin-right:12px;cursor:pointer}
.ae-apply__field input[type="file"]::file-selector-button:hover{border-color:var(--ae-muted)}
.ae-apply__group{margin:0 0 24px;padding:0;border:0;min-width:0}
.ae-apply__group>legend{padding:0 0 12px;font-size:1.0625rem;font-weight:650;color:var(--ae-ink)}
.ae-apply__panel>.ae-apply__group:last-child,.ae-apply__panel>.ae-apply__field:last-child{margin-bottom:0}

/* ── Choice tiles ─────────────────────────────────────────────────────────── */
.ae-apply__tile{display:flex;align-items:flex-start;gap:12px;margin-bottom:12px;padding:16px;cursor:pointer;
  background:var(--ae-surface);border:1px solid var(--ae-border-strong);border-radius:var(--ae-radius-lg)}
.ae-apply__tile input{flex:none;width:18px;height:18px;margin-top:3px;accent-color:var(--ae-brand)}
.ae-apply__tile-body{flex:1;min-width:0;display:block}
.ae-apply__tile .t{display:block;font-weight:650;color:var(--ae-ink)}
.ae-apply__tile .d{display:block;font-size:.875rem;color:var(--ae-muted)}
.ae-apply__tile:has(input:checked){border-color:var(--ae-brand);box-shadow:0 0 0 1px var(--ae-brand)}

/* ── The declaration, the refusal, the submit ─────────────────────────────── */
.ae-apply__check{display:flex;align-items:flex-start;gap:10px;margin-top:8px;font-size:.9375rem;color:var(--ae-ink);cursor:pointer}
.ae-apply__check input[type="checkbox"]{flex:none;width:18px;height:18px;margin-top:2px;accent-color:var(--ae-brand)}
.ae-apply__error{margin:16px 0 0;padding:12px 16px;border:1px solid var(--ae-error);border-radius:var(--ae-radius);
  background:color-mix(in srgb,var(--ae-error) 8%,var(--ae-surface));color:var(--ae-error);font-size:.9375rem}
.ae-apply__notice{margin:0;padding:16px 20px;border:1px solid var(--ae-border);border-left:3px solid var(--ae-success);
  border-radius:var(--ae-radius);background:var(--ae-surface);color:var(--ae-ink)}
.ae-apply__outcome{margin:0 0 24px;padding:16px 20px;border:1px solid var(--ae-border);border-left:3px solid var(--ae-accent);
  border-radius:var(--ae-radius);background:var(--ae-subtle);color:var(--ae-ink)}
.ae-apply__outcome p{margin:0}
.ae-apply__outcome p+p{margin-top:8px}
.ae-apply__muted{margin:0;color:var(--ae-muted)}
.ae-apply__links{display:flex;flex-wrap:wrap;gap:16px;margin-top:16px;font-size:.9375rem}
.ae-apply__links a,.ae-apply__inline a{color:var(--ae-link);text-underline-offset:.2em}
.ae-apply__links a:hover,.ae-apply__inline a:hover{color:var(--ae-link-hover)}
.ae-apply__submit{display:inline-flex;align-items:center;justify-content:center;width:100%;min-height:50px;margin-top:16px;
  padding:12px 24px;font:inherit;font-size:1rem;font-weight:650;cursor:pointer;
  background:var(--ae-accent);color:var(--ae-on-accent);border:1px solid var(--ae-accent);border-radius:var(--ae-radius);
  transition:background .15s ease,transform .15s ease}
.ae-apply__submit:hover:not(:disabled){background:var(--ae-accent-deep);border-color:var(--ae-accent-deep);transform:translateY(-1px)}
.ae-apply__submit:disabled{opacity:.55;cursor:not-allowed}

/* ── Chosen documents: one row per file, each saying what it proves ───────── */
.ae-apply__docs{list-style:none;margin:0 0 16px;padding:0;display:grid;gap:12px}
.ae-apply__doc{display:grid;gap:8px;padding:12px 14px;border:1px solid var(--ae-border);border-radius:var(--ae-radius)}
.ae-apply__doc-name{font-size:.875rem;font-weight:600;color:var(--ae-ink);overflow-wrap:anywhere}
.ae-apply__doc-size{font-size:.8125rem;color:var(--ae-muted);font-variant-numeric:tabular-nums}
.ae-apply__drop{justify-self:start;padding:6px 12px;font:inherit;font-size:.875rem;font-weight:600;cursor:pointer;
  background:transparent;color:var(--ae-ink);border:1px solid var(--ae-border-strong);border-radius:var(--ae-radius)}
.ae-apply__drop:hover{border-color:var(--ae-muted)}

/* ── The aside ────────────────────────────────────────────────────────────── */
.ae-apply__totals{background:var(--ae-surface);border:1px solid var(--ae-border);border-top:3px solid var(--ae-accent);
  border-radius:var(--ae-radius-lg);box-shadow:var(--ae-shadow-sm);padding:24px}
.ae-apply__totals h2{margin:0 0 16px;font-size:1.0625rem;font-weight:650;color:var(--ae-ink)}
.ae-apply__saving{margin:0 0 12px;font-weight:650;color:var(--ae-ink)}
.ae-apply__ticks{list-style:none;margin:16px 0 0;padding:16px 0 0;border-top:1px solid var(--ae-border);display:grid;gap:8px}
.ae-apply__ticks li{display:flex;align-items:flex-start;gap:8px;font-size:.8125rem;color:var(--ae-muted)}
.ae-apply__ticks svg{flex:none;margin-top:4px;color:var(--ae-success)}
.ae-apply__asks{list-style:none;margin:0;padding:0;display:grid;gap:6px;font-size:.875rem;color:var(--ae-muted)}
.ae-apply__asks li{display:flex;align-items:flex-start;gap:8px}
.ae-apply__asks li::before{content:"";flex:none;width:6px;height:6px;margin-top:8px;border-radius:999px;background:var(--ae-accent)}
.ae-apply__note{margin-top:16px;padding:12px 16px;border:1px solid var(--ae-border);border-left:3px solid var(--ae-accent);
  border-radius:var(--ae-radius);background:var(--ae-surface);font-size:.875rem;line-height:1.5;color:var(--ae-muted)}
.ae-apply__note strong{color:var(--ae-ink);font-weight:650}

@media(max-width:540px){
  .ae-apply__panel{padding:16px}
  .ae-apply__totals{padding:16px}
}
`;
